import { useCoursePreview } from "@/components/courses/content/use-course-preview";
import { useResponseVisit } from "@/components/assignments/coursework/use-response-visit";
import { useReadingVisit } from "@/components/courses/readings/use-reading-visit";
import { useTopicVisit } from "@/components/courses/topics/use-topic-visit";
import { useGuideVisit } from "@/components/courses/guide/use-guide-visit";
import { useMaterialVisit } from "@/components/courses/materials/use-material-visit";
import { useIntakeReceiptVisit } from "@/components/registrar/intake/intake-receipt-models";
import { useFeedbackVisit } from "@/components/assignments/coursework/use-feedback-visit";
import { useCourseDetailsVisit, useCourseTeamVisit, useCoursePublicationVisit, useCourseArchiveVisit } from "@/components/registrar/course/course-details-visit";
/**
 * The one visit table (CASE-027's registry ruling). Each object an area owns
 * has one row: registered (the area draws its records as a visit) or a
 * fallback (its address opens the legacy page until that area ships).
 *
 * Every fallback row is a named deviation, never a pass. An area's PR replaces
 * its own rows with registered ones:
 *
 *   { kind: "registered", object: "person", owner: "A5", jobs: ["access"],
 *     noun: "Person", hosts: ["people"], useBoxes: usePersonVisit }
 */
import {
  courseWorkspacePath,
  registrarCoursePath,
} from "@/navigation/routes";

import { useInvitationVisit } from "@/components/invitations/InvitationVisit";
import { useRecoveryVisit } from "@/components/invitations/RecoveryBoxes";
import { useEnrollVisit } from "@/components/people/visit/enroll-visit";
import { useFacultyPersonVisit } from "@/components/faculty/FacultyVisit";
import { useThreadVisit,useNoticeVisit } from "@/components/inbox/inbox-visits";
import { useApplicantVisit } from "@/components/admissions/ApplicantVisit";

import type { VisitTarget } from "./registry";
import { useWorkVisit } from "@/components/assignments/coursework/use-work-visit";
import { useHistoryVisit } from "@/components/history/useHistoryVisit";
import { useRunVisit } from "@/components/imports/useRunVisit";
import { useSessionReviewVisit } from "@/components/session/boxes/use-session-review-visit";
import { useSubmissionVisit } from "@/components/assignments/coursework/use-submission-visit";

export const VISIT_TARGETS: readonly VisitTarget[] = [
  { kind: "registered", object: "course-guide", owner: "A2", jobs: [], noun: "Course guide", hosts: ["course-guide"], scope: "course", useBoxes: useGuideVisit },
  { kind: "registered", object: "course-preview", owner: "A2", jobs: ["draft", "published"], noun: "Learner preview", hosts: ["course-topics"], scope: "course", useBoxes: useCoursePreview },
  { kind: "registered", object: "response", owner: "A3", jobs: [], noun: "Response", hosts: ["coursework"], useBoxes: useResponseVisit },
  { kind: "registered", object: "receipt", owner: "A3", jobs: "record", noun: "Receipt", hosts: ["coursework"], useBoxes: useResponseVisit },
  { kind: "registered", object: "reading", owner: "A3", jobs: "record", noun: "Reading", hosts: ["course-readings", "course-topics", "materials"], scope: "course", useBoxes: useReadingVisit },
  { kind: "registered", object: "topic", owner: "A2", jobs: "record", noun: "Topic", hosts: ["course-topics"], scope: "course", useBoxes: useTopicVisit },
  { kind: "registered", object: "teaching-draft", owner: "A3", jobs: "record", noun: "Teaching draft", hosts: ["coursework"], scope: "course", useBoxes: useWorkVisit },
  { kind: "registered", object: "material", owner: "A3", jobs: "record", noun: "Material", hosts: ["materials", "syllabus"], scope: "course", useBoxes: useMaterialVisit },
  {
    kind: "registered", object: "feedback", owner: "A3", jobs: "record",
    noun: "Feedback", hosts: ["grade-scores"], useBoxes: useFeedbackVisit,
  },
  // A5, People and Admissions. The person record is a visit (A5.1); a pasted link lands on People.
  { kind: "registered", object: "person", owner: "A5", jobs: ["access", "edit", "archive", "offboard", "delete", "role", "teaching"], noun: "Person", hosts: ["register", "faculty-register"], useBoxes: useFacultyPersonVisit },
  // enroll:<person>/<course>: one learner's marks and attendance in one course, A4's learner marks mounted in place.
  { kind: "registered", object: "enroll", owner: "A5", jobs: "record", noun: "Enrollment", hosts: ["register"], useBoxes: useEnrollVisit },
  {
    kind: "registered",
    object: "applicant",
    owner: "A5",
    jobs: [],
    noun: "Applicant",
    hosts: ["applicant-register"],
    useBoxes: useApplicantVisit,
  },
  { kind: "registered", object: "invitation", owner: "A5", jobs: [], noun: "Invitation", hosts: ["invitation-register"], useBoxes: useInvitationVisit },
  { kind: "registered", object: "recovery", owner: "A5", jobs: [], noun: "Identity recovery", hosts: ["identity-recovery"], useBoxes: useRecoveryVisit },
  // A6, the Registrar: the course record, Course History and Historical Imports.
  // Details, team and publication are also hosted on the teaching side, in
  // Manage course (`course-details-<course>`, `course-team-<course>`,
  // `publication-<course>`, #1393).
  { kind: "registered", object: "intake", owner: "A6", jobs: [], noun: "Intake receipt", hosts: ["intake-confirm", "intake-courses"], useBoxes: useIntakeReceiptVisit },
  { kind: "registered", object: "course", owner: "A6", jobs: ["details"], noun: "Section", hosts: ["sections", "course-details"], scope: "course", useBoxes: useCourseDetailsVisit },
  { kind: "registered", object: "course", owner: "A6", jobs: ["team"], noun: "Section", hosts: ["sections", "course-team"], scope: "course", useBoxes: useCourseTeamVisit },
  { kind: "registered", object: "course", owner: "A6", jobs: ["publication"], noun: "Section", hosts: ["sections", "publication", "course-details"], scope: "course", useBoxes: useCoursePublicationVisit },
  { kind: "registered", object: "course", owner: "A6", jobs: ["archive"], noun: "Section", hosts: ["sections"], scope: "course", useBoxes: useCourseArchiveVisit },
  {
    kind: "fallback",
    object: "course",
    owner: "A6",
    jobs: [],
    noun: "Course",
    legacy: (level, shell) => {
      if (shell !== "admin") return courseWorkspacePath(shell, level.id);
      return registrarCoursePath(level.id);
    },
  },
  {
    kind: "registered",
    object: "course",
    owner: "A6",
    jobs: ["history"],
    noun: "Closed section",
    hosts: ["history-sections"],
    useBoxes: useHistoryVisit,
  },
  {
    kind: "registered",
    object: "run",
    owner: "A6",
    jobs: ["invite"],
    noun: "Import run",
    hosts: ["import-runs"],
    useBoxes: useRunVisit,
  },
  {
    kind: "registered",
    object: "import-receipt",
    owner: "A6",
    jobs: "record",
    noun: "Import receipt",
    hosts: ["import-runs"],
    useBoxes: useRunVisit,
  },
  // A7: exact scoped conversations and notices, read under the visiting seat.
  {kind:"registered",object:"thread",owner:"A7",jobs:"record",noun:"Conversation",
    hosts:["private-messages","teaching-roster","teaching-team","conversations"],useBoxes:useThreadVisit},
  {kind:"registered",object:"notice",owner:"A7",jobs:[],noun:"Notice",
    hosts:["notices","registrar-overview"],useBoxes:useNoticeVisit},
  // A3 owns the canonical review and its actor-private field recovery.
  // `work:<assignment>/review` names an assignment; `work:<course>/create` names
  // the course it authors in, so only create is held to the page's course.
  { kind: "registered", object: "work", owner: "A3", jobs: ["review"], noun: "Work", hosts: ["coursework", "review"], useBoxes: useWorkVisit },
  { kind: "registered", object: "work", owner: "A3", jobs: ["create"], noun: "Work", hosts: ["coursework", "review"], scope: "course", useBoxes: useWorkVisit },
  // A2: a session is a workspace, so only its Review is visited (O10).
  {
    kind: "registered",
    object: "session",
    owner: "A2",
    jobs: ["review"],
    noun: "Session",
    hosts: ["follow-up"],
    useBoxes: useSessionReviewVisit,
  },
  // A1's attention caller names an exact attempt; A3 keeps its review controller.
  {
    kind: "registered",
    object: "submission",
    owner: "A3",
    jobs: ["review"],
    noun: "Attempt",
    hosts: ["teaching-attention", "review"],
    useBoxes: useSubmissionVisit,
  },
];
